<template>
  <div v-if="showAdminWarning" class="p-16 bg-bg-warning text-text-warning type-sm">
    This component is not available on corporate domains
  </div>
  <div
    v-else-if="!isNonEcomSite"
    v-editable="blok"
    class="CategoryListingBlock layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div>
      <div
        v-if="blok.headingTitle || blok.headingButtonText"
        class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16"
        :class="{'flex-col text-center' : blok.headingAlign === 'center'}"
      >
        <component :is="blok.headerTag ?? 'h2'" class="type-headline-xl"> {{ blok.headingTitle }}</component>
        <Nuxt-link
          :to="$sbLinkFix(blok.headingButtonLink)"
          :target="$sbLinkTarget(blok.headingButtonLink)"
        >
          <div
            v-if="blok.headingButtonText"
            class="btn btn--primary"
            :class="{
              'btn--secondary': blok.headingButtonStyle === 'secondary',
              'btn--underline': blok.headingButtonStyle === 'underline',
            }"
          >
            {{ blok.headingButtonText }}
          </div>
        </Nuxt-link>
      </div>
      <component
        :is="buttonsScroll ? SideScroll : 'div'"
        v-if="blok.design === 'buttons'"
      >
        <div
          class="flex flex-row items-center gap-8"
          :class="buttonsScroll ? 'mobOnly:pl-12' : 'flex-wrap justify-center'"
        >
          <nuxt-link
            v-for="item in blok.listItems"
            :key="item._uid"
            :to="itemLink(item)"
            class="shrink-0"
          >
            <div
              class="
              flex justify-center items-center type-headline-md h-40 desk:h-56 px-16 desk:px-24 border
            border-border-inverse hover:bg-bg-inverse hover:text-text-inverse transition duration-300 ease-in-out cursor-pointer"
            >
              {{ item.category }}
            </div>
          </nuxt-link>
          <div v-if="buttonsScroll" class="sideScrollRightPad desk:hidden" />
        </div>
      </component>
      <component
        :is="uiStore.isMobile ? SideScroll:'div'"
        v-if="blok.design === 'buttons-swiper'"
      >
        <div class="flex gap-12 mobOnly:pl-12 desk:flex-wrap">
          <nuxt-link
            v-for="item in blok.listItems"
            :key="item._uid"
            :to="itemLink(item)"
            class="shrink-0"
          >
            <div class="btn btn--secondary"> {{ item.category }}</div>
          </nuxt-link>

          <div class="sideScrollRightPad desk:hidden" />
        </div>
      </component>
      <div
        v-if="!blok.design?.startsWith('buttons') && ((blok.swiperMobile && isMobile) || (itemCount > 6))"
        class="relative"
      >
        <client-only>
          <swiper-container
            class="swiper-cover desk:!overflow-hidden !overflow-visible w-full desk:!pb-40 !pb-32"
            :space-between="8"
            :slides-per-view="uiStore.isMobile ? 2.1 : 6"
            :loop="false"
            :pagination="{
              clickable: true,
              dynamicBullets: true,
            }"
            :navigation="true"
            :a11y-prev-slide-message="$t('wcag.arialabel.previousSlide')"
            :a11y-next-slide-message="$t('wcag.arialabel.nextSlide')"
            :a11y-pagination-bullet-message="$t('wcag.arialabel.goToSlide')"
          >
            <swiper-slide
              v-for="item in blok.listItems"
              :key="item._uid"
              class="flex flex-col items-center mb-24 desk:mb-32 deskLarge:!overflow-hidden desk:cursor-grab desk:active:cursor-grabbing"
            >
              <nuxt-link
                :to="itemLink(item)"
              >
                <div class="flex flex-col items-center group">
                  <div class="relative mb-12">
                    <nuxt-img
                      v-if="item.image && item.image.filename"
                      preset="standard"
                      loading="lazy"
                      sizes="75vw desk:600px"
                      class="aspect-portrait object-cover h-full w-full opacity-100 group-hover:opacity-80 transition-all duration-500 ease-out"
                      :class="item.focalPoint"
                      :src="item.image.filename"
                      :alt="item.image.alt"
                    />
                  </div>
                  <div class="type-headline-md group-hover:underline underline-offset-4 decoration-1">
                    {{ item.category }}
                  </div>
                </div>
              </nuxt-link>
            </swiper-slide>
          </swiper-container>
        </client-only>
      </div>
      <div
        v-else-if="blok.design === 'images'"
        class="grid gap-x-8 gap-y-24"
        :class="[
          'grid-cols-[repeat(auto-fit,_minmax(150px,_1fr))] desk:grid-cols-[' + blok.listItems.length + ']'
        ]"
      >
        <nuxt-link
          v-for="item in blok.listItems"
          :key="item._uid"
          class="w-full"
          :to="itemLink(item)"
        >
          <div class="w-full flex flex-col items-center gap-12 group">
            <nuxt-img
              v-if="item.image && item.image.filename"
              preset="standard"
              loading="lazy"
              sizes="mob375:50vw mob390:50vw mob414:50vw desk:600px"
              class="aspect-portrait object-cover h-full w-full opacity-100 group-hover:opacity-80 transition-all duration-500 ease-out"
              :class="item.focalPoint"
              :src="item.image.filename"
              :alt="item.image.alt"
            />
            <div class="type-headline-md text-center type-headline-md group-hover:underline underline-offset-4 decoration-1">
              {{ item.category }}
            </div>
          </div>
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  StoryBlokAsset,
  StoryBlokLink,
  ProductSelector,
  DatasourceObjectPosition,
  DatasourceHeadlineTypes
} from '#constants/types/storyblok';

import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import useVisualAppearancePlugin from '~/composables/useVisualAppearancePlugin';
import SideScroll from '~/components/globals/SideScroll.vue';
import { useGlobalContentStore } from '~/store/globalContent';

const { isNonEcomSite, showAdminWarning } = useNonEcomGuard();
const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);
const { $sbLinkFix, $sbLinkCheck } = useNuxtApp();
const globalContent = useGlobalContentStore();

interface ListItem {
  _uid: string;
  category: string;
  image: StoryBlokAsset;
  link: StoryBlokLink;
  flagText: string;
  productLink: ProductSelector[]
  focalPoint: DatasourceObjectPosition;
}

interface Props {
  _uid: string;
  title: string;
  textAlign: 'left' | 'center';
  swiperMobile: boolean;
  design: 'images' | 'images-center' | 'logo-images' | 'buttons' | 'buttons-swiper',
  logoImage: StoryBlokAsset,
  buttonText: string,
  link: StoryBlokLink,
  listItems: ListItem[];
// Heading fields
  headingTitle: string;
  headerTag?: DatasourceHeadlineTypes;
  headingButtonText: string;
  headingButtonLink: StoryBlokLink;
  headingButtonStyle: 'primary' | 'secondary' | 'underline';
  headingAlign: 'left' | 'center';
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const buttonsScroll = computed(() => (props.blok.swiperMobile && isMobile.value));

const itemCount = computed(() => {
  if (!isMobile.value && (props.blok.design === 'images')) {
    return props.blok.listItems.length;
  } else {
    return 0;
  }
});

const itemLink = (item: ListItem) => {

  if (item.productLink &&
    item.productLink[0]?.product &&
    typeof item.productLink[0].product !== 'string' &&
    item.productLink[0].product.product !== null) {
    return `${globalContent.getPrependPathForManualLinks}/${globalContent.getProductBasePath}/${item.productLink[0].product.product.url}`;
  }
  if ($sbLinkCheck(item.link)) {
    return $sbLinkFix(item.link);
  }
  return null;
};

</script>
